import type { FunctionalComponent } from '@stencil/core'
import { h } from '@stencil/core'
import { ComponentView } from '../../../types/types'

export type WidgetType = 'rtr-article-booking' | 'rtr-search' | 'rtr-article-details' | 'rtr-page' | 'rtr-order'

export interface WidgetPanelProps {
  widgetType: WidgetType
  widgetView: ComponentView
  onWidgetTypeChange: (type: WidgetType) => void
  onWidgetViewChange: (view: ComponentView) => void
}

// Widget type configurations with their available views
const widgetConfigs: Record<WidgetType, { label: string; views: ComponentView[] }> = {
  'rtr-article-booking': {
    label: 'Article Booking',
    views: [ComponentView.Default, ComponentView.Calendar, ComponentView.Slots, ComponentView.Cards]
  },
  'rtr-search': {
    label: 'Search',
    views: [ComponentView.Default, ComponentView.Cards, ComponentView.Slots]
  },
  'rtr-article-details': {
    label: 'Article Details',
    views: [ComponentView.Default, ComponentView.Details]
  },
  'rtr-page': {
    label: 'Product Page',
    views: [ComponentView.Default]
  },
  'rtr-order': {
    label: 'Order',
    views: [ComponentView.Order]
  }
}

// View labels for better UX
const viewLabels: Record<ComponentView, string> = {
  [ComponentView.Default]: 'Default',
  [ComponentView.Calendar]: 'Calendar',
  [ComponentView.Slots]: 'Time Slots',
  [ComponentView.Cards]: 'Cards',
  [ComponentView.Details]: 'Details',
  [ComponentView.Checkout]: 'Checkout',
  [ComponentView.Order]: 'Order',
  [ComponentView.Booking]: 'Booking',
  [ComponentView.BookingCTA]: 'Booking CTA',
  [ComponentView.Cart]: 'Cart',
  [ComponentView.Config]: 'Config',
  [ComponentView.NewConfig]: 'New Config',
  [ComponentView.Item]: 'Item',
  [ComponentView.List]: 'List',
  [ComponentView.Search]: 'Search'
}

export const WidgetPanel: FunctionalComponent<WidgetPanelProps> = ({ widgetType, widgetView, onWidgetTypeChange, onWidgetViewChange }) => {
  const currentConfig = widgetConfigs[widgetType]
  const availableViews = currentConfig.views

  return (
    <div class="debug-toolbar-section">
      <div class="debug-toolbar-control">
        <div class="flex items-center justify-between gap-2">
          <label htmlFor="widgetType">Widget Type</label>
        </div>
        <select
          id="widgetType"
          onChange={e => onWidgetTypeChange((e.target as HTMLSelectElement).value as WidgetType)}
        >
          {Object.entries(widgetConfigs).map(([type, config]) => (
            <option
              value={type}
              key={type}
              selected={widgetType === type}
            >
              {config.label}
            </option>
          ))}
        </select>
      </div>

      <div class="debug-toolbar-control">
        <div class="flex items-center justify-between gap-2">
          <label htmlFor="widgetView">Widget View</label>
        </div>
        <select
          id="widgetView"
          onChange={e => onWidgetViewChange((e.target as HTMLSelectElement).value as ComponentView)}
        >
          {availableViews.map(view => (
            <option
              value={view}
              key={view}
              selected={widgetView === view}
            >
              {viewLabels[view]}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}
